import type { gcpProject } from '../types/gcpProject'
import type { gcpProjectIndexItem } from '../types/gcpProject'

const UUID_PATTERN =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export const normalizePlatformGcpProjectRef = (
	ref: string | { label?: string; value?: string } | undefined
): string | undefined => {
	if (ref == null) {
		return undefined
	}
	if (typeof ref === 'object') {
		return (ref.value || ref.label || '').trim() || undefined
	}
	return ref.trim() || undefined
}

export const resolveGcpProjectUuid = (
	platformGcpProjectRef: string | undefined,
	approvedProjects: Array<gcpProjectIndexItem>
): string | undefined => {
	if (!platformGcpProjectRef) {
		return undefined
	}

	if (UUID_PATTERN.test(platformGcpProjectRef)) {
		return platformGcpProjectRef
	}

	const refLower = platformGcpProjectRef.toLowerCase()
	const match = approvedProjects.find(
		(project) =>
			project.gcpProjectName?.toLowerCase() === refLower ||
			project.gcpProjectID?.toLowerCase() === refLower
	)

	return match?.gcpProjectID
}

export type GkeSubnetApprovalPrefill = {
	subnetwork: string
	subnetworkRange: string
	controlPlaneRange: string
	ipRangePods: string
	ipRangeServices: string
	ipRangePodsCidr: string
	ipRangeServicesCidr: string
}

type ResourceSubnet = {
	resourceType?: string
	isGke?: boolean
	subnetName?: string
	podRangeName?: string
	svcRangeName?: string
	subnetRangeCidr?: string
	podRangeCidr?: string
	svcRangeCidr?: string
}

const getPrimaryGkeResourceSubnet = (
	details: Record<string, unknown>
): ResourceSubnet | null => {
	const subnets = details.resourceSubnets
	if (!Array.isArray(subnets) || subnets.length === 0) {
		return null
	}
	const gkeSubnet = subnets.find(
		(entry) =>
			entry &&
			typeof entry === 'object' &&
			((entry as ResourceSubnet).isGke === true ||
				(entry as ResourceSubnet).resourceType === 'GKE')
	)
	return (gkeSubnet as ResourceSubnet) || (subnets[0] as ResourceSubnet)
}

export type GkeSubnetPrefillResult =
	| { status: 'not_applicable' }
	| { status: 'ready'; values: GkeSubnetApprovalPrefill }
	| { status: 'missing'; message: string }

const trim = (value: unknown): string =>
	typeof value === 'string' ? value.trim() : ''

const isSubnetRequired = (details: Record<string, unknown>): boolean => {
	const value = details.subnetRequired
	return value === true || trim(value).toLowerCase() === 'yes'
}

const isGkeInfrastructure = (details: Record<string, unknown>): boolean => {
	if (trim(details.infrastructureType).toUpperCase() === 'GKE') {
		return true
	}
	return Boolean(
		trim(details.controlPlaneSubnet) ||
			trim(details.secondaryPodsRange) ||
			trim(details.secondaryServicesRange)
	)
}

const hasGkeSubnetCidrs = (details: Record<string, unknown>): boolean =>
	Boolean(
		trim(details.primarySubnet) &&
			trim(details.controlPlaneSubnet) &&
			trim(details.secondaryPodsRange) &&
			trim(details.secondaryServicesRange)
	)

export const resolveGkeSubnetPlatformApprovalPrefill = (
	gcpProject: gcpProject | undefined
): GkeSubnetPrefillResult => {
	if (!gcpProject) {
		return { status: 'not_applicable' }
	}

	const details = (gcpProject.details || {}) as Record<string, unknown>

	if (!isSubnetRequired(details) || !isGkeInfrastructure(details)) {
		return { status: 'not_applicable' }
	}

	if (!hasGkeSubnetCidrs(details)) {
		return {
			status: 'missing',
			message:
				'Subnets are not created for the linked GCP project. On the GCP project approval page, save all required GKE subnet CIDRs, then try again.'
		}
	}

	const resourceSubnet = getPrimaryGkeResourceSubnet(details)
	const subnetwork =
		trim(details.subnetName) || trim(resourceSubnet?.subnetName) || ''
	const ipRangePods =
		trim(details.podRangeName) || trim(resourceSubnet?.podRangeName) || ''
	const ipRangePodsCidr = trim(details.secondaryPodsRange) || ''
	const ipRangeServices =
		trim(details.svcRangeName) || trim(resourceSubnet?.svcRangeName) || ''
	const ipRangeServicesCidr = trim(details.secondaryServicesRange) || ''
	

	if (!subnetwork) {
		return {
			status: 'missing',
			message:
				'Subnet name is not available for the linked GCP project. The name is created when the GCP project is approved and provisioned. Re-approve the GCP project after saving GKE subnet details, or enter the subnet name from the member project repository.'
		}
	}

	return {
		status: 'ready',
		values: {
			subnetwork,
			subnetworkRange: trim(details.primarySubnet),
			controlPlaneRange: trim(details.controlPlaneSubnet),
			ipRangePods: ipRangePods || trim(details.secondaryPodsRange),
			ipRangePodsCidr: ipRangePodsCidr,
			ipRangeServices: ipRangeServices || trim(details.secondaryServicesRange),
			ipRangeServicesCidr: ipRangeServicesCidr
		}
	}
}
